import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import { SlackWorkspaceRef } from "../SlackService";

import { slackQueryKeys } from "./slackQueryKeys";

export function useDisconnectSlackWorkspace(orgId?: string) {
  const { slackService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workspace: SlackWorkspaceRef) =>
      slackService.disconnectWorkspace(workspace, resolvedOrgId),
    onSuccess: (_data, workspace) => {
      // The deleted link's channel listing describes nothing; drop it rather than refetch it.
      // Other workspaces' caches are untouched.
      queryClient.removeQueries({
        queryKey: slackQueryKeys.workspaceChannels(resolvedOrgId, workspace),
      });
      // The two reads that outlive the disconnect: the connection list (whose refetch unmounts
      // the picker) and the allowlist (which loses this workspace's entries). Returned so
      // query-core awaits it before settling the mutation, which holds `isPending` — and so the
      // Disconnect button's progress indicator — until the rendered list matches the server.
      return Promise.all([
        queryClient.refetchQueries({
          queryKey: slackQueryKeys.connections(resolvedOrgId),
        }),
        queryClient.refetchQueries({
          queryKey: slackQueryKeys.outboundChannels(resolvedOrgId),
        }),
      ]);
    },
  });
}
